/**
 * Каталог иконок для новостных страниц БЗ — SVG-эмодзи в `/public/emoji_news`.
 * Хранение в `document_meta.icon` (varchar 16) — короткий код `e-<name>` плюс
 * опциональный 1-char color-suffix `:<key>` (например, `e-rocket:r`).
 */

export type NewsEmojiGroupId =
  | 'releases'
  | 'ideas'
  | 'content'
  | 'chat'
  | 'analytics'
  | 'status'
  | 'tech'
  | 'seasons'
  | 'misc';

export interface NewsEmojiGroup {
  id: NewsEmojiGroupId;
  label: string;
  /** ID эмодзи-репрезентанта группы для таб-кнопки. */
  representativeId: string;
}

export const NEWS_EMOJI_GROUPS: readonly NewsEmojiGroup[] = [
  { id: 'releases', label: 'Релизы', representativeId: 'e-rocket' },
  { id: 'ideas', label: 'Идеи', representativeId: 'e-bulb' },
  { id: 'content', label: 'Контент', representativeId: 'e-book' },
  { id: 'chat', label: 'Коммуникации', representativeId: 'e-chat' },
  { id: 'analytics', label: 'Аналитика', representativeId: 'e-chartup' },
  { id: 'status', label: 'Статусы', representativeId: 'e-check' },
  { id: 'tech', label: 'Технические', representativeId: 'e-gear' },
  { id: 'seasons', label: 'Сезоны', representativeId: 'e-tulip' },
  { id: 'misc', label: 'Прочее', representativeId: 'e-heart' },
];

export interface NewsEmoji {
  /** Уникальный код, который хранится в `document_meta.icon` (например, `e-rocket`). */
  id: string;
  /** Относительный путь от `/public/emoji_news/` (для seasons — `seasons/<period>/<name>.svg`). */
  filename: string;
  /** Подпись для tooltip / aria-label. */
  label: string;
  group: NewsEmojiGroupId;
}

export const NEWS_EMOJIS: readonly NewsEmoji[] = [
  // Релизы
  { id: 'e-rocket', filename: '02-rocket.svg', label: 'Ракета', group: 'releases' },
  { id: 'e-sparkle', filename: '01-sparkle.svg', label: 'Искра', group: 'releases' },
  { id: 'e-party', filename: '03-party.svg', label: 'Праздник', group: 'releases' },
  { id: 'e-fire', filename: '04-fire.svg', label: 'Огонь', group: 'releases' },
  { id: 'e-bolt', filename: '06-bolt.svg', label: 'Молния', group: 'releases' },
  { id: 'e-star', filename: '12-star.svg', label: 'Звезда', group: 'releases' },
  { id: 'e-gift', filename: '16-gift.svg', label: 'Подарок', group: 'releases' },
  { id: 'e-trophy', filename: '13-trophy.svg', label: 'Кубок', group: 'releases' },

  // Идеи
  { id: 'e-bulb', filename: '05-bulb.svg', label: 'Идея', group: 'ideas' },
  { id: 'e-target', filename: '07-target.svg', label: 'Цель', group: 'ideas' },
  { id: 'e-gem', filename: '49-gem.svg', label: 'Алмаз', group: 'ideas' },
  { id: 'e-puzzle', filename: '53-puzzle.svg', label: 'Пазл', group: 'ideas' },
  { id: 'e-sprout', filename: '52-sprout.svg', label: 'Росток', group: 'ideas' },

  // Контент
  { id: 'e-book', filename: '29-book.svg', label: 'Книга', group: 'content' },
  { id: 'e-note', filename: '23-note.svg', label: 'Заметка', group: 'content' },
  { id: 'e-pencil', filename: '47-pencil.svg', label: 'Карандаш', group: 'content' },
  { id: 'e-clip', filename: '54-clip.svg', label: 'Скрепка', group: 'content' },
  { id: 'e-bookmark', filename: '41-bookmark.svg', label: 'Закладка', group: 'content' },
  { id: 'e-folder', filename: '32-folder.svg', label: 'Папка', group: 'content' },
  { id: 'e-mail', filename: '45-mail.svg', label: 'Письмо', group: 'content' },

  // Коммуникации
  { id: 'e-chat', filename: '22-chat.svg', label: 'Чат', group: 'chat' },
  { id: 'e-mic', filename: '36-mic.svg', label: 'Микрофон', group: 'chat' },
  { id: 'e-headphones', filename: '35-headphones.svg', label: 'Наушники', group: 'chat' },
  { id: 'e-handshake', filename: '46-handshake.svg', label: 'Рукопожатие', group: 'chat' },
  { id: 'e-bell', filename: '11-bell.svg', label: 'Колокольчик', group: 'chat' },

  // Аналитика
  { id: 'e-chartup', filename: '15-chartup.svg', label: 'График вверх', group: 'analytics' },
  { id: 'e-search', filename: '30-search.svg', label: 'Поиск', group: 'analytics' },
  { id: 'e-map', filename: '39-map.svg', label: 'Карта', group: 'analytics' },
  { id: 'e-hourglass', filename: '38-hourglass.svg', label: 'Песочные часы', group: 'analytics' },
  { id: 'e-calendar', filename: '21-calendar.svg', label: 'Календарь', group: 'analytics' },
  { id: 'e-pin', filename: '14-pin.svg', label: 'Кнопка', group: 'analytics' },
  { id: 'e-bars', filename: '28-bars.svg', label: 'Бары', group: 'analytics' },

  // Статусы
  { id: 'e-check', filename: '18-check.svg', label: 'Готово', group: 'status' },
  { id: 'e-checklist', filename: '55-checklist.svg', label: 'Чек-лист', group: 'status' },
  { id: 'e-warn', filename: '19-warn.svg', label: 'Внимание', group: 'status' },
  { id: 'e-lock', filename: '20-lock.svg', label: 'Замок', group: 'status' },
  { id: 'e-unlock', filename: '33-unlock.svg', label: 'Открыто', group: 'status' },
  { id: 'e-key', filename: '44-key.svg', label: 'Ключ', group: 'status' },
  { id: 'e-flag', filename: '40-flag.svg', label: 'Флаг', group: 'status' },
  { id: 'e-thumb', filename: '42-thumb.svg', label: 'Палец вверх', group: 'status' },

  // Технические
  { id: 'e-tools', filename: '27-tools.svg', label: 'Инструменты', group: 'tech' },
  { id: 'e-gear', filename: '31-gear.svg', label: 'Шестерёнка', group: 'tech' },
  { id: 'e-bug', filename: '08-bug.svg', label: 'Баг', group: 'tech' },
  { id: 'e-robot', filename: '48-robot.svg', label: 'Робот', group: 'tech' },
  { id: 'e-link', filename: '24-link.svg', label: 'Ссылка', group: 'tech' },

  // Сезоны (весна / лето / осень / зима)
  { id: 'e-tulip', filename: 'seasons/spring/01-tulip.svg', label: 'Тюльпан', group: 'seasons' },
  { id: 'e-butterfly', filename: 'seasons/spring/02-butterfly.svg', label: 'Бабочка', group: 'seasons' },
  { id: 'e-umbrella', filename: 'seasons/spring/03-umbrella.svg', label: 'Зонтик', group: 'seasons' },
  { id: 'e-rainbow', filename: 'seasons/spring/04-rainbow.svg', label: 'Радуга', group: 'seasons' },
  { id: 'e-bird', filename: 'seasons/spring/05-bird.svg', label: 'Птица', group: 'seasons' },
  { id: 'e-branch', filename: 'seasons/spring/06-branch.svg', label: 'Ветка', group: 'seasons' },
  { id: 'e-sun', filename: 'seasons/summer/07-sun.svg', label: 'Солнце', group: 'seasons' },
  { id: 'e-icecream', filename: 'seasons/summer/08-icecream.svg', label: 'Мороженое', group: 'seasons' },
  { id: 'e-melon', filename: 'seasons/summer/09-watermelon.svg', label: 'Арбуз', group: 'seasons' },
  { id: 'e-cocktail', filename: 'seasons/summer/10-cocktail.svg', label: 'Коктейль', group: 'seasons' },
  { id: 'e-shades', filename: 'seasons/summer/11-sunglasses.svg', label: 'Очки', group: 'seasons' },
  { id: 'e-boat', filename: 'seasons/summer/12-boat.svg', label: 'Лодка', group: 'seasons' },
  { id: 'e-maple', filename: 'seasons/autumn/13-maple.svg', label: 'Клён', group: 'seasons' },
  { id: 'e-pumpkin', filename: 'seasons/autumn/14-pumpkin.svg', label: 'Тыква', group: 'seasons' },
  { id: 'e-mushroom', filename: 'seasons/autumn/15-mushroom.svg', label: 'Гриб', group: 'seasons' },
  { id: 'e-acorn', filename: 'seasons/autumn/16-acorn.svg', label: 'Жёлудь', group: 'seasons' },
  { id: 'e-pinecone', filename: 'seasons/autumn/17-pinecone.svg', label: 'Шишка', group: 'seasons' },
  { id: 'e-mug', filename: 'seasons/autumn/18-mug-scarf.svg', label: 'Какао', group: 'seasons' },
  { id: 'e-snowflk', filename: 'seasons/winter/19-snowflake.svg', label: 'Снежинка', group: 'seasons' },
  { id: 'e-snowman', filename: 'seasons/winter/20-snowman.svg', label: 'Снеговик', group: 'seasons' },
  { id: 'e-fir', filename: 'seasons/winter/21-fir.svg', label: 'Ёлка', group: 'seasons' },
  { id: 'e-mitten', filename: 'seasons/winter/22-mitten.svg', label: 'Варежка', group: 'seasons' },
  { id: 'e-cocoa', filename: 'seasons/winter/23-cocoa.svg', label: 'Какао', group: 'seasons' },
  { id: 'e-hat', filename: 'seasons/winter/24-hat.svg', label: 'Шапка', group: 'seasons' },

  // Прочее
  { id: 'e-heart', filename: '17-heart.svg', label: 'Сердце', group: 'misc' },
  { id: 'e-palette', filename: '09-palette.svg', label: 'Палитра', group: 'misc' },
  { id: 'e-package', filename: '10-package.svg', label: 'Коробка', group: 'misc' },
  { id: 'e-play', filename: '26-play.svg', label: 'Play', group: 'misc' },
  { id: 'e-coffee', filename: '25-coffee.svg', label: 'Кофе', group: 'misc' },
  { id: 'e-camera', filename: '37-camera.svg', label: 'Камера', group: 'misc' },
  { id: 'e-plane', filename: '51-plane.svg', label: 'Самолёт', group: 'misc' },
  { id: 'e-dice', filename: '50-dice.svg', label: 'Кубик', group: 'misc' },
  { id: 'e-home', filename: '56-home.svg', label: 'Дом', group: 'misc' },
  { id: 'e-cloud', filename: '43-cloud.svg', label: 'Облако', group: 'misc' },
  { id: 'e-globe', filename: '34-globe.svg', label: 'Глобус', group: 'misc' },
];

const BY_ID: ReadonlyMap<string, NewsEmoji> = new Map(NEWS_EMOJIS.map((e) => [e.id, e]));

// ──────────────────────────────────────────────────────────────────────────
// Палитра «оттенков» для пользовательской смены цвета SVG-эмодзи.
//
// Реализация: применяем CSS-`filter` chain к `<img>` элементу (hue-rotate +
// saturate). Сохраняет градиентную структуру оригинального SVG, но сдвигает
// общую тональность в выбранную сторону. Для абсолютно точных «бренд-цветов»
// потребовался бы inline-SVG с подменой gradient stops; CSS-filter подход
// проще и работает достаточно хорошо для большинства иконок.
// ──────────────────────────────────────────────────────────────────────────

export type NewsEmojiColor = 'd' | 'r' | 'a' | 'c' | 'g' | 't' | 's' | 'v' | 'm';

export interface NewsEmojiColorDef {
  /** Однобуквенный код для сериализации в icon-suffix (`e-rocket:r`). */
  key: NewsEmojiColor;
  /** Подпись для tooltip. */
  label: string;
  /** Swatch-цвет для отображения в picker'е (примерное визуальное приближение). */
  swatch: string;
  /** CSS filter chain. Пусто = «оригинальные цвета». */
  filter: string;
}

export const NEWS_EMOJI_COLORS: readonly NewsEmojiColorDef[] = [
  { key: 'd', label: 'Оригинальный', swatch: 'conic-gradient(from 0deg, #f48fb1, #ffe082, #a5d6a7, #80deea, #b39ddb, #f48fb1)', filter: '' },
  { key: 'r', label: 'Розовый',     swatch: '#e91e63', filter: 'hue-rotate(310deg) saturate(1.4)' },
  { key: 'a', label: 'Янтарный',    swatch: '#ffb300', filter: 'hue-rotate(35deg) saturate(1.5) brightness(1.05)' },
  { key: 'c', label: 'Коралл',      swatch: '#ff7043', filter: 'hue-rotate(10deg) saturate(1.5)' },
  { key: 'g', label: 'Изумруд',     swatch: '#43a047', filter: 'hue-rotate(95deg) saturate(1.2)' },
  { key: 't', label: 'Бирюзовый',   swatch: '#26a69a', filter: 'hue-rotate(150deg) saturate(1.2)' },
  { key: 's', label: 'Небесный',    swatch: '#42a5f5', filter: 'hue-rotate(195deg) saturate(1.15)' },
  { key: 'v', label: 'Сливовый',    swatch: '#7e57c2', filter: 'hue-rotate(245deg) saturate(1.1)' },
  { key: 'm', label: 'Графит',      swatch: '#546e7a', filter: 'grayscale(1) brightness(0.95)' },
];

const COLOR_BY_KEY: ReadonlyMap<NewsEmojiColor, NewsEmojiColorDef> = new Map(
  NEWS_EMOJI_COLORS.map((c) => [c.key, c]),
);

/** Парсинг сериализованного значения `e-rocket:r` → `{ id, color }`. */
export function parseNewsEmojiValue(
  value: string | null | undefined,
): { id: string; color: NewsEmojiColor } | null {
  if (!value) return null;
  const colonIdx = value.indexOf(':');
  if (colonIdx < 0) {
    return BY_ID.has(value) ? { id: value, color: 'd' } : null;
  }
  const id = value.slice(0, colonIdx);
  const colorRaw = value.slice(colonIdx + 1);
  if (!BY_ID.has(id)) return null;
  const color = COLOR_BY_KEY.has(colorRaw as NewsEmojiColor)
    ? (colorRaw as NewsEmojiColor)
    : 'd';
  return { id, color };
}

/** Композиция id+color в сериализованную строку. Опускает суффикс если color='d'. */
export function composeNewsEmojiValue(id: string, color: NewsEmojiColor): string {
  if (color === 'd') return id;
  return `${id}:${color}`;
}

/** Это news-эмодзи (`e-<name>` или `e-<name>:<color>`)? */
export function isNewsEmojiCode(value: string | null | undefined): boolean {
  return parseNewsEmojiValue(value) !== null;
}

/** Получить URL SVG-файла news-эмодзи по сохранённому значению (с возможным color-суффиксом). */
export function getNewsEmojiUrl(value: string | null | undefined): string | null {
  const parsed = parseNewsEmojiValue(value);
  if (!parsed) return null;
  const e = BY_ID.get(parsed.id);
  return e ? `/emoji_news/${e.filename}` : null;
}

/** Получить подпись news-эмодзи (для tooltip / aria-label). */
export function getNewsEmojiLabel(value: string | null | undefined): string | null {
  const parsed = parseNewsEmojiValue(value);
  if (!parsed) return null;
  const e = BY_ID.get(parsed.id);
  return e ? e.label : null;
}

/** Получить CSS-filter для применения цвета к SVG. Пустая строка = «без фильтра». */
export function getNewsEmojiFilter(value: string | null | undefined): string {
  const parsed = parseNewsEmojiValue(value);
  if (!parsed) return '';
  return COLOR_BY_KEY.get(parsed.color)?.filter ?? '';
}
